import { useEffect, useState } from "react";
import { DataTable } from "../components/DataTable";
import { newsletterService } from "../services/newsletterService";
import { NewsletterResponse, NewsletterRequest } from "../interfaces/ApiResponse";


// =========================
// TYPE SAFE FOR DATATABLE
// =========================
//type NewsletterRow = NewsletterResponse & Record<string, unknown>;

type SubStatus = "Active" | "Unsubscribed";

// =========================
// SUCCESS MODAL
// =========================
function SuccessModal({ message, onClose }: { message: string; onClose: () => void }) {
  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,.45)",
        backdropFilter: "blur(4px)", zIndex: 99999,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: "#fff", borderRadius: 20, padding: "36px 32px",
        maxWidth: 400, width: "100%", textAlign: "center",
        boxShadow: "0 24px 60px rgba(0,0,0,.18)",
        animation: "nsModalIn .25s ease",
      }}>
        <style>{`@keyframes nsModalIn{from{opacity:0;transform:translateY(14px) scale(.97)}to{opacity:1;transform:none}}`}</style>
        <div style={{ fontSize: 48, marginBottom: 14 }}>✅</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#166534", marginBottom: 8 }}>Success</div>
        <div style={{ fontSize: 15, color: "#475569", marginBottom: 24 }}>{message}</div>
        <button
          onClick={onClose}
          style={{
            background: "linear-gradient(135deg,#22c55e,#16a34a)", color: "#fff",
            border: "none", borderRadius: 12, padding: "10px 28px",
            fontSize: 15, fontWeight: 700, cursor: "pointer",
          }}
        >
          OK
        </button>
      </div>
    </div>
  );
}

// =========================
// ADD MODAL
// =========================
function AddModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [source, setSource] = useState("Manual");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  async function handleAdd() {
    try {

      if (!email.trim() || !email.includes("@")) {
        setErr("Valid email required");
        return;
      }

      setLoading(true);

      const payload: NewsletterRequest = {
        email,
        name,
        source,
      };

      const response = await newsletterService.create(payload);

      if (!response.isSuccess) {
        setErr(response.message);
        return;
      }

      setSuccessMsg(response.message);
      setErr("");
      onSuccess();

    } catch (error: any) {
      setErr(
        error?.response?.data?.message ||
        "Something went wrong"
      );
    } finally {
      setLoading(false);
    }
  }

  if (successMsg) {
    return <SuccessModal message={successMsg} onClose={onClose} />;
  }

  return (
    <div className="mty-panel-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}>

      <div className="mty-panel" style={{ maxWidth: 440 }}>

        <div className="mty-panel-header">
          <div className="d-flex align-items-center gap-2">
            <div className="mst-icon-box">
              <i className="bi bi-person-plus"></i>
            </div>
            <div>
              <div className="fw-bold mst-title">Add Subscriber</div>
              <small className="mst-subtitle">Manually add subscriber</small>
            </div>
          </div>

          <button className="btn-close" onClick={onClose} />
        </div>

        <div className="mty-panel-body">

          {err && <div className="alert alert-danger">{err}</div>}

          <input
            className="form-control mb-2"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <input
            className="form-control mb-2"
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <select
            className="form-select"
            value={source}
            onChange={(e) => setSource(e.target.value)}
          >
            <option>Manual</option>
            <option>Homepage Footer</option>
            <option>Popup</option>
            <option>Checkout</option>
            <option>Blog</option>
          </select>

        </div>

        <div className="mty-panel-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>

          <button
            className="btn btn-primary"
            disabled={loading}
            onClick={handleAdd}
          >
            {loading ? "Saving..." : "Add Subscriber"}
          </button>
        </div>

      </div>
    </div>
  );
}

// =========================
// MAIN PAGE
// =========================
export function NewsletterPage() {

  const [subs, setSubs] = useState<NewsletterResponse[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [filterStatus, setFilterStatus] = useState<"All" | SubStatus>("All");
  const [deleteTarget, setDeleteTarget] = useState<NewsletterResponse | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  const loadSubscribers = async () => {
    const data = await newsletterService.getAll();
    setSubs(data); // ✅ direct array
  };

  useEffect(() => {
    loadSubscribers();
  }, []);

  const active = subs.filter(x => x.isActive).length;
  const inactive = subs.filter(x => !x.isActive).length;

  const filtered =
    filterStatus === "All"
      ? subs
      : subs.filter(x =>
        filterStatus === "Active"
          ? x.isActive
          : !x.isActive
      );

  const toggleStatus = async (id: number) => {
    await newsletterService.toggleStatus(id);
    loadSubscribers();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteLoading(true);
      await newsletterService.delete(deleteTarget.subscriberID);
      await loadSubscribers();
      setDeleteTarget(null);
      setDeleteSuccess(true);
    } catch (error) {
      console.error(error);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="d-grid gap-4">

      {/* FILTER */}
      <div className="card shadow-sm">
        <div className="card-body d-flex justify-content-between">

          <div className="d-flex gap-2">
            {["All", "Active", "Unsubscribed"].map(x => (
              <button
                key={x}
                className={`btn btn-sm ${filterStatus === x ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setFilterStatus(x as any)}
              >
                {x}
              </button>
            ))}
          </div>

          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowAdd(true)}
          >
            Add Subscriber
          </button>

        </div>
      </div>

      {/* TABLE */}
      <DataTable<Record<string, unknown>>
        title="Newsletter Subscribers"
        rows={filtered as unknown as Record<string, unknown>[]}
        rowKey={(r) => String(r.subscriberID)}
        searchPlaceholder="Search..."
        exportFileName="newsletter"
        deleteItemLabel="Subscriber"

        columns={[
          { key: "email", label: "Email", sortable: true },
          { key: "name", label: "Name", sortable: true },
          { key: "source", label: "Source" },

          {
            key: "status",
            label: "Status",
            render: (r) => (
              <span className={`badge ${r.isActive ? "text-bg-success" : "text-bg-secondary"}`}>
                {r.isActive ? "Active" : "Unsubscribed"}
              </span>
            )
          },

          {
            key: "actions",
            label: "Actions",
            render: (r) => (
              <div className="d-flex gap-2">

                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => toggleStatus(r.subscriberID as number)}
                >
                  Toggle
                </button>

                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => setDeleteTarget(r as unknown as NewsletterResponse)}
                >
                  Delete
                </button>

              </div>
            )
          }
        ]}
      />

      {/* ADD MODAL */}
      {showAdd && (
        <AddModal
          onClose={() => setShowAdd(false)}
          onSuccess={loadSubscribers}
        />
      )}

      {/* DELETE CONFIRM MODAL */}
      {deleteTarget && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(15,23,42,.5)",
            backdropFilter: "blur(4px)", zIndex: 99999,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setDeleteTarget(null); }}
        >
          <div style={{
            background: "#fff", borderRadius: 20, padding: "36px 32px",
            maxWidth: 420, width: "100%", textAlign: "center",
            boxShadow: "0 24px 60px rgba(0,0,0,.18)",
            animation: "nsModalIn .25s ease",
          }}>
            <style>{`@keyframes nsModalIn{from{opacity:0;transform:translateY(14px) scale(.97)}to{opacity:1;transform:none}}`}</style>

            <div style={{
              width: 64, height: 64, borderRadius: 20, background: "#fef2f2",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 16px", fontSize: 28,
            }}>🗑️</div>

            <div style={{ fontSize: 20, fontWeight: 800, color: "#1e293b", marginBottom: 8 }}>
              Delete Subscriber?
            </div>
            <div style={{ fontSize: 14, color: "#64748b", marginBottom: 6 }}>
              You are about to remove:
            </div>
            <div style={{
              fontSize: 15, fontWeight: 700, color: "#ef4444",
              background: "#fef2f2", borderRadius: 10, padding: "8px 16px",
              marginBottom: 24, display: "inline-block",
            }}>
              {deleteTarget.email}
            </div>
            <div style={{ fontSize: 13, color: "#94a3b8", marginBottom: 28 }}>
              This action cannot be undone.
            </div>

            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleteLoading}
                style={{
                  height: 44, padding: "0 24px", borderRadius: 12,
                  border: "1.5px solid #e2e8f0", background: "#f8fafc",
                  fontSize: 14, fontWeight: 700, color: "#475569", cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteLoading}
                style={{
                  height: 44, padding: "0 28px", borderRadius: 12,
                  border: "none", background: "linear-gradient(135deg,#ef4444,#dc2626)",
                  fontSize: 14, fontWeight: 700, color: "#fff", cursor: "pointer",
                  opacity: deleteLoading ? 0.7 : 1,
                }}
              >
                {deleteLoading ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE SUCCESS MODAL */}
      {deleteSuccess && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(15,23,42,.45)",
            backdropFilter: "blur(4px)", zIndex: 99999,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
          }}
          onClick={() => setDeleteSuccess(false)}
        >
          <div style={{
            background: "#fff", borderRadius: 20, padding: "36px 32px",
            maxWidth: 380, width: "100%", textAlign: "center",
            boxShadow: "0 24px 60px rgba(0,0,0,.18)",
            animation: "nsModalIn .25s ease",
          }}>
            <div style={{ fontSize: 48, marginBottom: 14 }}>✅</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#166534", marginBottom: 8 }}>Deleted Successfully</div>
            <div style={{ fontSize: 14, color: "#475569", marginBottom: 24 }}>
              The subscriber has been removed.
            </div>
            <button
              onClick={() => setDeleteSuccess(false)}
              style={{
                background: "linear-gradient(135deg,#22c55e,#16a34a)", color: "#fff",
                border: "none", borderRadius: 12, padding: "10px 28px",
                fontSize: 15, fontWeight: 700, cursor: "pointer",
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

    </div>
  );
}